/**
 * DecisionBar — K1 (UX-Inventar): the one decision pattern at every KI→Mensch handoff.
 * One clear primary (green/ok), one or two alternatives, "Ablehnen" deliberately set apart
 * (a valid decision, not an error → never red). Each click writes a protocol entry server-side.
 */
import type { ReactNode } from 'react';
import { Button } from '../ui/Button';
import { cn } from '../../lib/cn';

export interface DecisionAction {
  label: string;
  icon?: ReactNode;
  onClick?: () => void;
}

interface DecisionBarProps {
  /** primary (green) — e.g. "Freigeben" / "Bestätigen & verarbeiten" */
  primary: DecisionAction;
  /** 1–2 alternatives — e.g. "Rückfrage an Versicherer", "Klärungs-Aufgabe" */
  alternatives?: DecisionAction[];
  /** deliberately offset, neutral (not red) — e.g. "Ablehnen" */
  reject?: DecisionAction;
  className?: string;
}

export function DecisionBar({ primary, alternatives = [], reject, className }: DecisionBarProps) {
  return (
    <div className={cn('flex flex-wrap items-center gap-2', className)}>
      <Button onClick={primary.onClick} className="bg-sem-ok text-white hover:bg-sem-ok/90">
        {primary.icon}
        {primary.label}
      </Button>
      {alternatives.map((a) => (
        <Button key={a.label} variant="outline" size="default" onClick={a.onClick}>
          {a.icon}
          {a.label}
        </Button>
      ))}
      {reject && (
        <>
          <div className="flex-1" />
          <Button variant="ghost" size="default" onClick={reject.onClick} className="text-muted-foreground">
            {reject.icon}
            {reject.label}
          </Button>
        </>
      )}
    </div>
  );
}
